import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiBook, FiFlag, FiBrain, FiHeart, FiShield, FiArrowRight } = FiIcons;

const About = () => {
  const [showRandomFacts, setShowRandomFacts] = useState(false);

  const credentials = [
    {
      icon: FiBook,
      title: "Academic Excellence",
      description: "Trained at Purdue University, a top-tier program",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: FiAward,
      title: "Elite Recognition",
      description: "David Knox Fellowship recipient (awarded to less than 0.02% of individuals)",
      color: "from-accent-gold to-accent-yellow"
    },
    {
      icon: FiFlag,
      title: "Presidential Honor",
      description: "Outstanding Student Award recipient from President Obama",
      color: "from-red-500 to-pink-500"
    },
    {
      icon: FiBrain,
      title: "Neuroscience Authority",
      description: "Former NASA scientist with deep expertise in brain function and cognitive retraining",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: FiHeart,
      title: "Medical Understanding",
      description: "Deep knowledge of how addiction changes brain structure and function",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: FiShield,
      title: "Recovery Partnership",
      description: "Believes people in recovery are the heroes of their own stories, deserving skilled support",
      color: "from-accent-magenta to-accent-purple"
    }
  ];

  const randomFacts = [
    { text: "Mildly obsessed with French fries", id: 1 },
    { text: "Whodunit lover", id: 2 }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Guarantee Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="bg-gradient-to-r from-accent-magenta to-accent-purple rounded-3xl p-8 lg:p-12 text-white mb-16">
            <h2 className="text-3xl lg:text-4xl font-playfair font-bold mb-6">
              See Stronger Recovery Skills in 90 Days, Guaranteed.
            </h2>
            <p className="text-lg lg:text-xl leading-relaxed max-w-4xl mx-auto">
              We believe every brain can heal and grow stronger with the right training. If you don't see measurable improvement in your executive function skills and recovery confidence within 90 days, we'll work with you until you do—or offer a refund. Because your recovery matters more than our profit, and your life is too precious to leave to chance.
            </p>
          </div>
        </motion.div>

        {/* About Sanna */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <h3 className="text-3xl lg:text-4xl font-playfair font-bold text-gray-900 mb-6 relative">
                Meet <span id="sanna-name" className="text-accent-magenta">Sanna</span> Darwish, M.S. CCC-SLP — Your Recovery Brain Specialist
              </h3>
              
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                ❤️ <strong>Empathetic Specialist:</strong> Provides a safe, non-judgmental, and science-backed space to address the neurological roots of addictive behaviors.
              </p>
              
              <div className="bg-accent-light/30 p-6 rounded-2xl mb-6">
                <p className="text-gray-800 font-medium text-lg text-center">
                  This isn't generic addiction counseling. It's specialized cognitive therapy designed to rebuild the brain skills that make lasting recovery possible.
                </p>
              </div>
              
              <p className="text-xl font-semibold text-accent-magenta text-center mb-6">
                Let's strengthen your recovery foundation for lifelong freedom—starting today.
              </p>

              <div className="text-center">
                <motion.button
                  onClick={() => setShowRandomFacts(!showRandomFacts)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-accent-gold text-white px-6 py-3 rounded-full font-semibold hover:bg-accent-gold/90 transition-colors duration-200"
                >
                  {showRandomFacts ? 'Hide' : 'Unlock'} random facts
                </motion.button>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753170088935-blob"
                alt="Sanna Darwish, M.S. CCC-SLP - Recovery Brain Specialist"
                className="w-full h-96 object-cover rounded-3xl shadow-2xl"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              
              {/* Random Facts Overlay */}
              <AnimatePresence>
                {showRandomFacts && (
                  <div className="absolute inset-0 pointer-events-none">
                    {randomFacts.map((fact, index) => (
                      <motion.div
                        key={fact.id}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.2 }}
                        className={`absolute bg-white rounded-2xl p-4 shadow-lg border-2 border-accent-gold ${
                          index === 0 ? 'top-4 -left-8' : 'bottom-4 -right-8'
                        }`}
                        style={{ maxWidth: '200px' }}
                      >
                        <div className="text-sm font-medium text-gray-800">{fact.text}</div>
                        
                        {/* Arrow pointing to Sanna */}
                        <div className={`absolute w-0 h-0 ${
                          index === 0 
                            ? 'bottom-[-8px] left-8 border-l-[8px] border-r-[8px] border-t-[8px] border-l-transparent border-r-transparent border-t-accent-gold'
                            : 'top-[-8px] right-8 border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent border-b-accent-gold'
                        }`}></div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        {/* Credentials Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
        >
          {credentials.map((credential, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
            >
              <div className={`w-12 h-12 bg-gradient-to-r ${credential.color} rounded-xl flex items-center justify-center mb-4`}>
                <SafeIcon icon={credential.icon} className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">{credential.title}</h4>
              <p className="text-gray-600 text-sm leading-relaxed">{credential.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <motion.button
            onClick={() => scrollToSection('contact')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-accent-magenta text-white px-8 py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-2 mx-auto hover:bg-accent-magenta/90 transition-colors duration-200"
          >
            Book Your Recovery Breakthrough Call
            <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default About;